import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, MapPin, ArrowRight } from "lucide-react";
import DashboardShell from "@/layouts/ProviderDashboardLayout";
import api from "@/lib/api";
import StatusPill from "@/components/marketplace/StatusPill";
import RebookButton from "@/components/marketplace/RebookButton";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

export default function BookingsListPage() {
  const [role, setRole] = useState("customer");
  const [status, setStatus] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useDocumentTitle("My bookings");

  useEffect(() => {
    setLoading(true);
    const params = { role };
    if (status) params.status = status;
    api.get("/bookings/mine", { params }).then((r) => setItems(r.data || [])).catch(() => setItems([])).finally(() => setLoading(false));
  }, [role, status]);

  return (
    <DashboardShell>
      <div className="max-w-5xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Bookings</div>
            <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Your bookings</h1>
          </div>
          <div className="flex items-center gap-2">
            <select value={role} onChange={(e) => setRole(e.target.value)} className="rounded-full border border-slate-200 px-4 py-2 text-sm bg-white" data-testid="bookings-role">
              <option value="customer">As customer</option>
              <option value="provider">As provider</option>
            </select>
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-full border border-slate-200 px-4 py-2 text-sm bg-white" data-testid="bookings-status">
              <option value="">All statuses</option>
              {["payment_pending", "awaiting_provider_response", "confirmed", "scheduled", "in_progress", "awaiting_customer_completion_approval", "completed", "canceled"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div className="text-[#252A34]/60">Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center">
            <div className="font-display text-lg text-[#071b22] font-semibold">No bookings yet.</div>
            <div className="text-sm text-[#252A34]/70 mt-1">Book a Gulf Coast pro to see them here.</div>
            <Link to="/book-local" className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-5 py-2.5 text-sm font-medium">Book local <ArrowRight className="h-4 w-4" /></Link>
          </div>
        ) : (
          <div className="grid gap-3">
            {items.map((b) => (
              <Link key={b.id} to={`/dashboard/bookings/${b.id}`} className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5 flex items-center gap-4 hover:border-[#165a71]/40 transition-colors" data-testid={`booking-row-${b.id}`}>
                <div className="h-14 w-14 rounded-xl bg-[#165a71]/10 flex items-center justify-center text-[#165a71]"><CalendarClock className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-semibold text-[#071b22] truncate">{b.service_title || "Booking"}</div>
                  <div className="text-xs text-[#252A34]/60 mt-1 flex flex-wrap items-center gap-2">
                    <span><CalendarClock className="h-3.5 w-3.5 inline" /> {b.scheduled_start ? new Date(b.scheduled_start).toLocaleString() : "—"}</span>
                    {b.address_snapshot && <span>· <MapPin className="h-3.5 w-3.5 inline" /> {b.address_snapshot.city}, {b.address_snapshot.state}</span>}
                    <span>· ${(b.total_estimate || 0).toFixed(2)}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <StatusPill status={b.status} />
                  {b.status === "completed" && role === "customer" && <RebookButton booking={b} />}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </DashboardShell>
  );
}
